import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Bike } from "lucide-react";

const STATUS_STYLES = {
  pending_payment: "bg-amber-100 text-amber-700",
  confirmed: "bg-emerald-100 text-emerald-700",
  active: "bg-blue-100 text-blue-700",
  completed: "bg-secondary text-muted-foreground",
  cancelled: "bg-red-100 text-red-700",
};

const PLAN_UNIT = { daily: "day", weekly: "week", monthly: "month" };

export default function Dashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/bookings/my").then((res) => setBookings(res.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
      <span className="label-caps text-primary">Driver dashboard</span>
      <h1 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">Hi, {user?.name?.split(" ")[0]} 👋</h1>
      <p className="mt-2 text-muted-foreground text-lg">Track your rentals and manage your bookings.</p>

      <div className="mt-8 flex items-center justify-between">
        <h2 className="text-xl font-bold">Your bookings</h2>
        <Link to="/bikes"><Button className="bg-primary hover:bg-[#1D4ED8] text-white font-bold" data-testid="dashboard-new-booking">+ New rental</Button></Link>
      </div>

      {loading ? (
        <div className="mt-6 space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={`skeleton-${i}`} className="h-24 rounded-xl bg-secondary animate-pulse" />)}</div>
      ) : bookings.length === 0 ? (
        <div className="mt-6 border border-dashed border-border rounded-2xl p-12 text-center">
          <Bike className="w-10 h-10 text-muted-foreground mx-auto" />
          <p className="mt-4 font-bold text-lg">No rentals yet</p>
          <p className="text-muted-foreground">Browse the fleet and book your first bike.</p>
          <Link to="/bikes"><Button className="mt-5 bg-primary hover:bg-[#1D4ED8] text-white font-bold">Browse bikes</Button></Link>
        </div>
      ) : (
        <div className="mt-6 space-y-4" data-testid="bookings-list">
          {bookings.map((b) => (
            <div key={b.id} data-testid={`booking-${b.id}`} className="flex flex-col sm:flex-row sm:items-center gap-4 border border-border rounded-xl p-4 bg-white">
              <img src={b.bike_image} alt={b.bike_name} className="w-full sm:w-28 h-28 sm:h-20 object-cover rounded-lg" />
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h3 className="font-bold text-lg">{b.bike_name}</h3>
                  <Badge className={`capitalize ${STATUS_STYLES[b.status] || "bg-secondary"}`}>{b.status.replace("_", " ")}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1 capitalize">{b.plan} · {b.quantity} {PLAN_UNIT[b.plan]}{b.quantity > 1 ? "s" : ""} · from {b.start_date}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-black font-display">£{b.total_amount}</p>
                <p className="text-xs text-muted-foreground">{b.payment_status === "paid" ? "Paid" : "Awaiting payment"}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
